import { useState } from 'react';
import { format } from 'date-fns';
import { parseDate } from '@/lib/localizedDateUtils';
import { ptBR } from 'date-fns/locale';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { CustomInstallment } from '@/hooks/finance/usePaymentAgreements';

interface CustomInstallmentsViewerProps {
  installments: CustomInstallment[];
  currency?: 'USD' | 'BRL';
  defaultExpanded?: boolean;
}

function formatCurrency(value: number, currency: 'USD' | 'BRL' = 'USD') {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

export function CustomInstallmentsViewer({
  installments,
  currency = 'USD',
  defaultExpanded = false,
}: CustomInstallmentsViewerProps) {
  const [isOpen, setIsOpen] = useState(defaultExpanded);

  const total = installments.reduce((sum, inst) => sum + inst.amount, 0);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mt-2">
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
        >
          {isOpen ? (
            <>
              <ChevronUp className="mr-1 h-3 w-3" />
              Ocultar {installments.length} parcelas
            </>
          ) : (
            <>
              <ChevronDown className="mr-1 h-3 w-3" />
              Ver {installments.length} parcelas
            </>
          )}
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent className="mt-2">
        <div className="rounded-md border bg-background">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-8 w-12 text-xs">#</TableHead>
                <TableHead className="h-8 text-xs">Valor</TableHead>
                <TableHead className="h-8 text-xs">Vencimento</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {installments.map((installment, index) => (
                <TableRow key={index} className="hover:bg-muted/50">
                  <TableCell className="py-1.5 text-xs text-muted-foreground">
                    {index + 1}
                  </TableCell>
                  <TableCell className="py-1.5 text-xs font-medium">
                    {formatCurrency(installment.amount, currency)}
                  </TableCell>
                  <TableCell className="py-1.5 text-xs">
                    {format(parseDate(installment.due_date) || new Date(), 'dd/MM/yyyy', { locale: ptBR })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="border-t px-2 py-1.5 text-xs">
            <span className="text-muted-foreground">Total:</span>{' '}
            <span className="font-semibold">{formatCurrency(total, currency)}</span>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
